import React, {useContext, useEffect} from 'react';
import {useSelector} from 'react-redux';

import {DisabledText} from 'src/components/profile-tab-structure';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {selectUserBrowserInfo} from 'src/modules/profile/user/services/user-profile-slice';
import {getUserBrowserInfo} from 'src/openapi/admin/apis/GetUserBrowserInfoApi';
import {useAppDispatch} from 'src/store/hooks';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {DiffTime} from '@pb/kit-component-diff-time';
import {FormSection} from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import {Scroll} from '@pb/kit-component-scroll';
import {LocaleContext} from '@pb/kit-context-locale';

import {BrowserRegistrationSkeleton} from './browser-registration-skeleton';
import {GenerateRegistrationCodeController} from './generate-registration-code.controller';
import {RemoveBrowserController} from './remove-browser-controller';


type Props = {
    userId: number,
};

export const BrowserRegistrationSection: React.FC<Props> = ({userId}) => {
    const apiDispatch = useAppDispatch();
    const browserInfo = useSelector(selectUserBrowserInfo);
    const {locale} = useContext(LocaleContext);

    useEffect(() => {
        if (!browserInfo) {
            apiDispatch(getUserBrowserInfo({userId}));
        }
    }, [userId, browserInfo, apiDispatch]);

    return (
        <Skeleton view={<BrowserRegistrationSkeleton/>} shown={!browserInfo}>
            <FormSection
                headline="Browser Registration"
                info={headline => (
                    <Item grow={1}>
                        <Group justify="space-between">
                            {headline}
                            <GenerateRegistrationCodeController userId={userId}/>
                        </Group>
                    </Item>
                )}>
                <Group direction="v">
                    {browserInfo?.enabled ? (
                        <Group gap="s">
                            <Icon type={Icons.CHECK}/>
                            Browser registration enabled
                        </Group>
                    ) : (
                        <DisabledText>
                            Browser registration is disabled
                        </DisabledText>
                    )}
                    {browserInfo?.places?.length ? (
                        <Scroll>
                            <Group direction="v" gap="s">
                                <Group gap="none" alignItems="center">
                                    <WidthItem width={25}><strong>#</strong></WidthItem>
                                    <WidthItem width={110}><strong>IP address</strong></WidthItem>
                                    <WidthItem width={110}><strong>Browser type</strong></WidthItem>
                                    <WidthItem width={110}><strong>Registered</strong></WidthItem>
                                    <WidthItem width={110}><strong>Last accessed</strong></WidthItem>
                                    <WidthItem width={20}/>
                                </Group>
                                {browserInfo.places.map((info, index) => {
                                    const {ipAddress, browserName, browserVersion, lastActive, registrationDate} = info;
                                    return (
                                        <Group key={index} gap="none" alignItems="center">
                                            <WidthItem width={25}>{index + 1}</WidthItem>
                                            <WidthItem width={110}>{ipAddress}</WidthItem>
                                            <WidthItem width={110}>{browserName} {browserVersion}</WidthItem>
                                            <WidthItem width={110}>
                                                {registrationDate ? (
                                                    <Tooltip
                                                        color="light"
                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                            <div ref={anchorRef} {...getAnchorProps()}>
                                                                <DiffTime
                                                                    createdDate={registrationDate}
                                                                    fullUnits={true}
                                                                />
                                                            </div>
                                                        )}>
                                                        {FormattedDate.format({
                                                            value: lastActive,
                                                            format: mapDateFormat(locale.dateFormat, 'WORD'),
                                                        })}
                                                    </Tooltip>
                                                ) : '--'}
                                            </WidthItem>
                                            <WidthItem width={110}>
                                                {lastActive ? (
                                                    <Tooltip
                                                        color="light"
                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                            <div ref={anchorRef} {...getAnchorProps()}>
                                                                <DiffTime createdDate={lastActive} fullUnits={true}/>
                                                            </div>
                                                        )}>
                                                        {FormattedDate.format({
                                                            value: lastActive,
                                                            format: mapDateFormat(locale.dateFormat, 'WORD'),
                                                        })}
                                                    </Tooltip>
                                                ) : '--'}
                                            </WidthItem>
                                            <Item>
                                                <RemoveBrowserController userId={userId} userPlaceId={info.browserId}/>
                                            </Item>
                                        </Group>
                                    );
                                })}
                            </Group>
                        </Scroll>
                    ) : (
                        <span>
                            There are no browsers have been registered yet.
                        </span>
                    )}
                </Group>
            </FormSection>
        </Skeleton>
    );
};
